import { Column } from '@/shared/components/tables/DataTable';
import { DataTable } from '@/shared/components/tables/DataTable';
import { FindEstagiarioDto } from '@/api.types.extra';
import { ReadPersonDto, ReadEstagiarioDto } from '@/api.types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { readEstagiarioFroTransparency } from '../people/estagiario.service';
import { toast } from 'react-toastify';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { useEffect, useState } from 'react';
import { validaCPF } from '@/shared/utils/cpfs';

interface Props { }

/**
 * Lista de estagiários para servir ao portal da transparência.
 */
export default function PublicTraineeIndexPage({ }: Props) {

  const [registros, setRegistros] = useState<[ReadPersonDto[] | ReadEstagiarioDto[], number]>();
  const [statusFilter, setStatusFilter] = useState<'ativo' | 'inativo' | 'todos'>('ativo');
  const tableState = useDataTableState("nome");

  async function handleBuscar() {
    const filterCpf = tableState.filter && !isNaN(Number(tableState.filter)) ? tableState.filter : '';
    const filterNome = tableState.filter && isNaN(Number(tableState.filter)) ? tableState.filter : '';

    // Se for estagiário, usa o endpoint específico de estagiários
    const filters: FindEstagiarioDto & { status?: 'ativo' | 'inativo' | 'todos' } = {
      skip: ((tableState.currentPage - 1) * tableState.pageSize),
      take: tableState.pageSize,
      nome: filterNome,
      cpf: filterCpf,
      comEstagios: true,
      comTelefones: true,
      ...(statusFilter !== 'todos' ? { status: statusFilter } : {}),
      orderBy: tableState.sortConfig.direction,
      orderField: tableState.sortConfig?.key || 'nome',
    } as FindEstagiarioDto & { status?: 'ativo' | 'inativo' | 'todos' };

    try {
      const res = await readEstagiarioFroTransparency(filters);
      // Converte ReadEstagiarioDto[] para ReadPersonDto[] para compatibilidade com as colunas
      const convertedData = res[0].map((estagiario: ReadEstagiarioDto) => ({
        id: estagiario.pessoa.id,
        cpf: estagiario.pessoa.cpf,
        nome: estagiario.pessoa.nome,
        dataNascimento: estagiario.pessoa.dataNascimento,
        email: estagiario.pessoa.email,
        emailInstitucional: estagiario.pessoa.emailInstitucional,
        estadocivil: 0 as const,
        podeserresponsavel: false,
        senha: '', // Campo obrigatório mas não usado na listagem
        estagiario: {
          id: estagiario.id,
          ativo: estagiario.ativo,
          anotacao: estagiario.anotacao,
          estagios: estagiario.estagios,
        },
        editadoPor: estagiario.editadoPor,
        editadoEm: estagiario.editadoEm,
      })) as ReadPersonDto[];
      setRegistros([convertedData, res[1]] as [ReadPersonDto[], number]);
    } catch (error) {
      toast.error(String(error));
    }
    return;

  }

  useEffect(() => {
    handleBuscar();
  }, [
    tableState.currentPage,
    tableState.pageSize,
    tableState.filter,
    tableState.sortConfig?.key,
    tableState.sortConfig?.direction,
    statusFilter]);

  return (
    <>
      <div className="row">
        <div className="col-md-8">
          <h3>Estagiários</h3>
        </div>
        <hr className="pb-4" />

        <div className="row mb-3">
          <div className="col-md-3">
            <label htmlFor="statusFilter" className="form-label">
              Filtrar por Status:
            </label>
            <select
              id="statusFilter"
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'ativo' | 'inativo' | 'todos')}
            >
              <option value="ativo">Apenas Ativos</option>
              <option value="inativo">Apenas Inativos</option>
              <option value="todos">Todos</option>
            </select>
          </div>
        </div>

        {registros &&
          <DataTable
            key="estagiarios"
            columns={colunas}
            data={registros?.[0] as ReadPersonDto[]}
            initialSortKey="nome"
            onChange={handleBuscar}
            disableColumnsFilter={false}
            disableExportButtons={false}
            serverSide={true}
            states={tableState}
            totalRecords={registros?.[1]}
            renderFilters={() => (<></>)}
          />
        }
      </div>
    </>
  );
}

const colunas: Column<ReadPersonDto>[] = [
  { key: "cpf", header: "CPF", render: (row) => validaCPF(row.cpf!, true), invisible: true },
  {
    key: "dataNascimento",
    header: "Aniversário",
    type: "date",
    invisible: true,
    render: (row) =>
      row.dataNascimento ? format(new Date(row.dataNascimento), "dd/MM/yyyy", { locale: ptBR }) : ''
  },
  {
    key: "nome", header: "Nome",
    render: (row) =>
      row.estagiario?.ativo
        ? row.nome
        : <span className="text-muted">{row.nome} (inativo)</span>
  },
  // {
  //   key: "estagiario.estagios.data_inicio_estagio",
  //   header: "Início do Estágio",
  //   type: "date",
  //   render: (row) =>
  //     row.estagiario?.estagios?.[0]?.data_inicio_estagio
  //       ? format(new Date(row.estagiario.estagios[0].data_inicio_estagio), "dd/MM/yyyy", { locale: ptBR })
  //       : "N/A"
  // },
  // {
  //   key: "estagiario.estagios.data_fim_estagio",
  //   header: "Fim do Estágio",
  //   type: "date",
  //   render: (row) =>
  //     row.estagiario?.estagios?.[0]?.data_fim_estagio
  //       ? format(new Date(row.estagiario.estagios[0].data_fim_estagio), "dd/MM/yyyy", { locale: ptBR })
  //       : "N/A"
  // },
  { key: "email", header: "E-mail", invisible: true },
  { key: "emailInstitucional", header: "E-mail Institucional", invisible: true },
  { key: "estagiario.estagios.0.email", header: "E-mail Estágio", invisible: true },
];